import { useRef } from 'react';
import { EmblaCarousel } from '@/components/elements/Carousel/Carousel';
import { ResponsiveImage } from '@/components/elements/ResponsiveImage';
import Container from '@/components/global/Container';
import { IComponentBlocksGallery, IUploadFile, IUploadFileEntity } from '@/graphql/__generated__/global/types';
import classNames from '@/lib/classNames';
import { getImageUrl } from '@/lib/getImageUrl';
import { VariantProps, cva } from 'class-variance-authority';
import { m as motion } from 'framer-motion';

type GridVariant = 2 | 3 | 4;

const galleryGridVariants: Record<GridVariant, string> = {
    2: 'grid-cols-2 lg:px-32 xl:px-48 2xl:px-64',
    3: 'grid-cols-[1fr_2fr_1fr] grid-rows-1 max-h-[600px]',
    4: 'grid-cols-[1fr_1fr_2fr] grid-rows-2 max-h-[600px]',
};

const portraitSizes = `(max-width: 639px) 75vw, (max-width: 1023px) 35vw, (max-width: 1723px) 25vw, 405px`;
const landscapeSizes = `(max-width: 639px) 75vw, (max-width: 1023px) 35vw, (max-width: 1723px) 50vw, 810px`;

type ImageVariantProps = VariantProps<typeof imageVariants>;
const imageVariants = cva('object-cover col-span-1 rounded-[15px]', {
    variants: {
        numImages: {
            2: 'w-full',
            3: 'h-full w-full',
            4: 'h-full w-full',
        },
        index: {
            0: 'col-start-1',
            1: 'col-start-2',
            2: '',
            3: 'col-start-3',
        },
    },
    compoundVariants: [
        {
            numImages: [2, 3],
            index: 2,
            class: 'col-start-3',
        },
        {
            numImages: 4,
            index: [0, 3],
            class: 'row-span-2 row-start-1',
        },
        {
            numImages: 4,
            index: 1,
            class: 'row-span-1 row-start-1',
        },
        {
            numImages: 4,
            index: 2,
            class: 'row-span-1 row-start-2 col-start-2',
        },
    ],
    defaultVariants: {
        numImages: 2,
        index: 2,
    },
});

export const BlockGallery = ({ galleryImages: images }: IComponentBlocksGallery) => {
    const ref = useRef<HTMLDivElement>(null);

    if (!images || !images.length) {
        return null;
    }

    const gridStyle = galleryGridVariants[images.length as GridVariant] ?? '';

    // If desktop view use the grid layout
    // on mobile we want to use carousel
    return (
        <>
            <div className="hidden lg:block">
                <Container>
                    <div className={classNames('relative grid items-center gap-5 h-full w-full', gridStyle)} ref={ref}>
                        {images.map((card, index) => {
                            const img = card.image.data as IUploadFileEntity;
                            if (!img) {
                                return null;
                            }

                            const { alternativeText, url, width, height } = img.attributes as IUploadFile;

                            const portrait = height && width ? height > width : false;
                            const ratio = portrait ? 5 / 7 : 4 / 3;

                            return (
                                <motion.div
                                    key={card.id ?? url}
                                    className={imageVariants({
                                        numImages: images.length as ImageVariantProps['numImages'],
                                        index: index as ImageVariantProps['index'],
                                    })}
                                    initial="hidden"
                                    whileInView="visible"
                                    variants={{
                                        visible: { opacity: 1, y: 10 },
                                        hidden: { opacity: 0, y: 0 },
                                    }}
                                    transition={{
                                        duration: 0.3,
                                        delay: index * 0.2,
                                        ease: 'easeInOut',
                                    }}
                                >
                                    <ResponsiveImage
                                        className="rounded-[15px]"
                                        alt={alternativeText ?? ''}
                                        aspectRatio={ratio}
                                        src={getImageUrl(img)}
                                        sizes={portrait ? portraitSizes : landscapeSizes}
                                    />
                                </motion.div>
                            );
                        })}
                    </div>
                </Container>
            </div>
            <div className="block lg:hidden">
                <EmblaCarousel
                    emblaOptions={{
                        align: 'center',
                        slidesToScroll: 1,
                        containScroll: 'trimSnaps',
                        loop: true,
                        inViewThreshold: 0,
                        dragFree: true,
                    }}
                >
                    {({ emblaRef }) => {
                        return (
                            <EmblaCarousel.Slides ref={emblaRef} className={`embla__gallery ${images.length > 2 && 'embla__gallery-multi'}`}>
                                {images?.map((image, index) => {
                                    const img = image.image.data as IUploadFileEntity;

                                    if (!img) {
                                        return null;
                                    }
                                    const { alternativeText, width, height } = img.attributes as IUploadFile;

                                    const portrait = height && width ? height > width : false;
                                    const ratio = portrait ? 5 / 7 : 4 / 3;

                                    return (
                                        <div className="embla__slide" key={index}>
                                            <ResponsiveImage
                                                alt={alternativeText ?? ''}
                                                aspectRatio={ratio}
                                                src={getImageUrl(img)}
                                                className="rounded-2xl h-auto"
                                                sizes={portrait ? portraitSizes : landscapeSizes}
                                            />
                                        </div>
                                    );
                                })}
                            </EmblaCarousel.Slides>
                        );
                    }}
                </EmblaCarousel>
            </div>
        </>
    );
};

export default BlockGallery;
